// Operator help guide — the in-app knowledge base (D2/D3). Structured bilingual
// content (Tamil-first, paired *_en / *_ta), the same pattern as the landing
// page's TIERS/FAQS. Prose lives here as data so the /help route can render +
// search it without a heavy i18n-json round-trip. Add an article = append to
// ARTICLES; the section + slug drive the URL (/help#slug) and the nav grouping.

export type HelpBlock =
  | { kind: 'p'; en: string; ta: string }
  | { kind: 'steps'; en: string[]; ta: string[] }
  | { kind: 'tip'; en: string; ta: string };

export interface HelpArticle {
  slug: string;
  section: string;
  title_en: string;
  title_ta: string;
  summary_en: string;
  summary_ta: string;
  body: HelpBlock[];
}

export interface HelpSection {
  key: string;
  title_en: string;
  title_ta: string;
}

export const HELP_SECTIONS: HelpSection[] = [
  { key: 'start', title_en: 'Getting started', title_ta: 'தொடங்குதல்' },
  { key: 'chits', title_en: 'Chits', title_ta: 'சீட்டுகள்' },
  { key: 'members', title_en: 'Members', title_ta: 'உறுப்பினர்கள்' },
  { key: 'money', title_en: 'Collections & money', title_ta: 'வசூல் & பணம்' },
  { key: 'comms', title_en: 'Reminders & WhatsApp', title_ta: 'நினைவூட்டல் & WhatsApp' },
  { key: 'reports', title_en: 'Reports', title_ta: 'அறிக்கைகள்' },
  { key: 'account', title_en: 'Plan & account', title_ta: 'திட்டம் & கணக்கு' },
];

export const ARTICLES: HelpArticle[] = [
  {
    slug: 'welcome',
    section: 'start',
    title_en: 'Welcome to ChitDesk',
    title_ta: 'ChitDesk-க்கு வரவேற்கிறோம்',
    summary_en: 'What ChitDesk does and how your workspace is set up.',
    summary_ta: 'ChitDesk என்ன செய்கிறது, உங்கள் வேலையகம் எப்படி அமைக்கப்படுகிறது.',
    body: [
      { kind: 'p', en: 'ChitDesk runs your chit-fund business — chits, members, collections, payouts, reminders and reports — in one place, in Tamil. Your data lives in its own private workspace, isolated from every other operator.', ta: 'ChitDesk உங்கள் சீட்டு வணிகத்தை — சீட்டுகள், உறுப்பினர்கள், வசூல், பணம் வழங்கல், நினைவூட்டல், அறிக்கைகள் — ஒரே இடத்தில், தமிழில் நடத்துகிறது. உங்கள் தரவு தனி, பாதுகாப்பான வேலையகத்தில் இருக்கும்.' },
      { kind: 'p', en: 'You do not have to move everything at once. Run one chit on ChitDesk alongside your paper book for a month, and switch fully only when you trust it.', ta: 'எல்லாவற்றையும் ஒரே நேரத்தில் மாற்ற வேண்டாம். ஒரு சீட்டை பதிவேட்டுடன் இணையாக ஒரு மாதம் நடத்துங்கள்; நம்பிக்கை வந்த பிறகே முழுமையாக மாறுங்கள்.' },
      { kind: 'tip', en: 'The Getting Started checklist on your dashboard walks you through the first few steps.', ta: 'டாஷ்போர்டில் உள்ள "தொடங்குதல்" பட்டியல் முதல் சில படிகளை வழிநடத்தும்.' },
    ],
  },
  {
    slug: 'first-chit',
    section: 'start',
    title_en: 'Create and publish your first chit',
    title_ta: 'உங்கள் முதல் சீட்டை உருவாக்கி வெளியிடுங்கள்',
    summary_en: 'Set the value, members, duration and monthly subscription, then publish.',
    summary_ta: 'மதிப்பு, உறுப்பினர்கள், காலம், மாதச் சந்தா அமைத்து வெளியிடுங்கள்.',
    body: [
      { kind: 'steps',
        en: ['Go to Chits → New chit.', 'Enter a name (Tamil + English), the number of members (pool size) and duration in months.', 'Set the monthly subscription amount and the collection day.', 'Save. The chit starts as a draft so you can review it.', 'Add members, then publish when ready.'],
        ta: ['சீட்டுகள் → புதிய சீட்டு என்பதற்குச் செல்லுங்கள்.', 'பெயர் (தமிழ் + ஆங்கிலம்), உறுப்பினர் எண்ணிக்கை, காலம் (மாதங்கள்) உள்ளிடுங்கள்.', 'மாதச் சந்தா தொகை மற்றும் வசூல் நாளை அமைக்கவும்.', 'சேமிக்கவும். சீட்டு வரைவாகத் தொடங்கும், சரிபார்க்கலாம்.', 'உறுப்பினர்களைச் சேர்த்து, தயாரானதும் வெளியிடுங்கள்.'] },
      { kind: 'tip', en: 'Pool size = how many members and how many months. A 20-member chit runs 20 cycles.', ta: 'உறுப்பினர் எண்ணிக்கை = எத்தனை உறுப்பினர், எத்தனை மாதம். 20 உறுப்பினர் சீட்டு 20 சுற்றுகள் நடக்கும்.' },
    ],
  },
  {
    slug: 'cycle-plan',
    section: 'chits',
    title_en: 'Set the cycle plan (who gets each month)',
    title_ta: 'சுற்று திட்டம் அமைக்கவும் (யார் எந்த மாதம்)',
    summary_en: 'Decide the fixed payout order for the chit.',
    summary_ta: 'சீட்டின் நிலையான பணம் வழங்கல் வரிசையை முடிவு செய்யுங்கள்.',
    body: [
      { kind: 'p', en: 'ChitDesk is built for fixed-order chits: you decide in advance which member receives the payout in each cycle. Open the chit and set the cycle plan — for every month, choose the recipient and the pay/receive amounts.', ta: 'ChitDesk நிலையான வரிசை சீட்டுகளுக்கானது: எந்த உறுப்பினர் எந்த சுற்றில் பணம் பெறுவார் என்பதை முன்கூட்டியே நீங்கள் முடிவு செய்கிறீர்கள். சீட்டைத் திறந்து, ஒவ்வொரு மாதத்திற்கும் பெறுநரையும் கட்ட/பெற தொகைகளையும் அமைக்கவும்.' },
      { kind: 'tip', en: 'If you leave it blank, ChitDesk fills a sensible default schedule you can edit later.', ta: 'காலியாக விட்டால், ChitDesk ஒரு இயல்புத் திட்டத்தை நிரப்பும்; பின்னர் திருத்தலாம்.' },
    ],
  },
  {
    slug: 'add-members',
    section: 'members',
    title_en: 'Add members to a chit',
    title_ta: 'சீட்டில் உறுப்பினர்களைச் சேர்க்கவும்',
    summary_en: 'Add a brand-new member or place an existing one into another chit.',
    summary_ta: 'புதிய உறுப்பினர் சேர்க்கலாம் அல்லது இருக்கும் ஒருவரை இன்னொரு சீட்டில் இணைக்கலாம்.',
    body: [
      { kind: 'p', en: 'A member is identified by phone number — one person, one record, even across many chits. The same member can join many chits, but holds only one position per chit.', ta: 'உறுப்பினர் தொலைபேசி எண்ணால் அடையாளம் காணப்படுகிறார் — ஒரு நபர், ஒரு பதிவு, பல சீட்டுகளிலும். ஒரே உறுப்பினர் பல சீட்டுகளில் சேரலாம், ஆனால் ஒரு சீட்டில் ஒரே இடம் மட்டும்.' },
      { kind: 'steps',
        en: ['Open the chit → Add member.', 'Choose "Existing" to reuse someone already in your books, or "New" to create a record.', 'For a new member, enter name and phone (the phone must be unique).', 'Assign their position in the chit.'],
        ta: ['சீட்டைத் திறந்து → உறுப்பினர் சேர்.', 'உங்கள் பதிவில் உள்ள ஒருவரை மீண்டும் பயன்படுத்த "இருக்கும்", புதியதாக உருவாக்க "புதிய" தேர்வு செய்யவும்.', 'புதிய உறுப்பினருக்கு பெயர் + தொலைபேசி உள்ளிடவும் (தொலைபேசி தனித்துவமாக இருக்க வேண்டும்).', 'சீட்டில் அவர்களின் இடத்தை ஒதுக்கவும்.'] },
    ],
  },
  {
    slug: 'record-collection',
    section: 'money',
    title_en: 'Record a collection',
    title_ta: 'வசூலைப் பதிவு செய்யுங்கள்',
    summary_en: 'Mark a member’s monthly subscription as paid.',
    summary_ta: 'உறுப்பினரின் மாதச் சந்தா செலுத்தப்பட்டதாகக் குறிக்கவும்.',
    body: [
      { kind: 'steps',
        en: ['Go to Dues to see who owes this month.', 'Tap a member and record the amount received (full or part).', 'Choose the method (cash, UPI, etc.). A receipt is generated automatically.'],
        ta: ['இந்த மாதம் யார் கட்ட வேண்டும் என்பதைக் காண "நிலுவை"-க்குச் செல்லுங்கள்.', 'உறுப்பினரைத் தட்டி, பெற்ற தொகையைப் பதிவு செய்யுங்கள் (முழு அல்லது பகுதி).', 'முறையைத் தேர்வு செய்யவும் (ரொக்கம், UPI...). ரசீது தானாக உருவாகும்.'] },
      { kind: 'tip', en: 'Every collection writes to the cash book and the member’s statement at the same time — nothing to reconcile by hand.', ta: 'ஒவ்வொரு வசூலும் ரொக்கப் புத்தகம் + உறுப்பினர் அறிக்கை இரண்டிலும் ஒரே நேரத்தில் பதிவாகும் — கையால் சரிபார்க்க வேண்டாம்.' },
    ],
  },
  {
    slug: 'cash-book',
    section: 'money',
    title_en: 'The cash book',
    title_ta: 'ரொக்கப் புத்தகம்',
    summary_en: 'Every rupee in and out, with a running balance.',
    summary_ta: 'உள்வரும் & வெளியேறும் ஒவ்வொரு ரூபாயும், நடப்பு இருப்புடன்.',
    body: [
      { kind: 'p', en: 'Transactions shows every entry — collections, payouts, commission and manual adjustments — with a running balance. You can add a manual cash entry for anything that happens outside a chit.', ta: '"பரிவர்த்தனைகள்" ஒவ்வொரு பதிவையும் — வசூல், பணம் வழங்கல், கமிஷன், கைமுறை சரிசெய்தல் — நடப்பு இருப்புடன் காட்டும். சீட்டுக்கு வெளியே நடப்பதற்கு கைமுறை ரொக்கப் பதிவு சேர்க்கலாம்.' },
    ],
  },
  {
    slug: 'payouts',
    section: 'money',
    title_en: 'Record a payout to the winner',
    title_ta: 'வெற்றியாளருக்குப் பணம் வழங்கலைப் பதிவு செய்யுங்கள்',
    summary_en: 'Disburse the cycle amount to the member whose turn it is.',
    summary_ta: 'முறை வந்த உறுப்பினருக்கு சுற்றுத் தொகையை வழங்குங்கள்.',
    body: [
      { kind: 'p', en: 'Open the chit’s current cycle and record the payout to the scheduled recipient. ChitDesk shows the net amount after your foreman commission, and the payout appears in the cash book and the member’s statement.', ta: 'சீட்டின் நடப்பு சுற்றைத் திறந்து, திட்டமிட்ட பெறுநருக்குப் பணம் வழங்கலைப் பதிவு செய்யுங்கள். ஃபோர்மேன் கமிஷனுக்குப் பிறகான நிகரத் தொகையை ChitDesk காட்டும்; பணம் வழங்கல் ரொக்கப் புத்தகம் + உறுப்பினர் அறிக்கையில் தோன்றும்.' },
    ],
  },
  {
    slug: 'ledger-photo',
    section: 'money',
    title_en: 'Import from a photo of your paper ledger',
    title_ta: 'உங்கள் பதிவேட்டின் புகைப்படத்திலிருந்து இறக்குமதி',
    summary_en: 'Snap your Tamil ledger page; ChitDesk reads and fills it in. (Growth plan)',
    summary_ta: 'தமிழ் பதிவேட்டுப் பக்கத்தைப் புகைப்படம் எடுங்கள்; ChitDesk படித்து நிரப்பும். (வளர்ச்சி திட்டம்)',
    body: [
      { kind: 'p', en: 'Take a clear photo of a ledger page. ChitDesk reads the names and amounts, shows you what it found to review, and on one confirm fills in the collections and assignments. You always review before anything is saved.', ta: 'பதிவேட்டுப் பக்கத்தின் தெளிவான புகைப்படம் எடுங்கள். ChitDesk பெயர்களையும் தொகைகளையும் படித்து, கண்டதை சரிபார்க்கக் காட்டும்; ஒரு உறுதிப்படுத்தலில் வசூல் + ஒதுக்கீடுகளை நிரப்பும். சேமிப்பதற்கு முன் நீங்கள் எப்போதும் சரிபார்க்கலாம்.' },
      { kind: 'tip', en: 'This feature is on the Growth plan and above.', ta: 'இந்த அம்சம் வளர்ச்சி திட்டம் மற்றும் மேல்.' },
    ],
  },
  {
    slug: 'connect-whatsapp',
    section: 'comms',
    title_en: 'Connect your own WhatsApp number + per-chit groups',
    title_ta: 'உங்கள் சொந்த WhatsApp எண் + சீட்டு குழுக்களை இணைக்கவும்',
    summary_en: 'Link your business number once; ChitDesk can create a group per chit.',
    summary_ta: 'உங்கள் வணிக எண்ணை ஒரு முறை இணைக்கவும்; ஒவ்வொரு சீட்டுக்கும் குழு உருவாக்கலாம்.',
    body: [
      { kind: 'steps',
        en: ['Go to Settings → Channels → WhatsApp.', 'Choose "Connect your own number" and scan the QR with your business WhatsApp.', 'Once linked, ChitDesk can create a WhatsApp group for each chit and add the members.'],
        ta: ['அமைப்புகள் → சேனல்கள் → WhatsApp.', '"சொந்த எண்ணை இணை" தேர்வு செய்து, உங்கள் வணிக WhatsApp மூலம் QR-ஐ ஸ்கேன் செய்யவும்.', 'இணைந்ததும், ஒவ்வொரு சீட்டுக்கும் WhatsApp குழுவை உருவாக்கி உறுப்பினர்களைச் சேர்க்கலாம்.'] },
      { kind: 'tip', en: 'Use a dedicated business number, not your personal WhatsApp. Own-number WhatsApp is included on every plan.', ta: 'தனிப்பட்ட WhatsApp அல்ல, தனி வணிக எண்ணைப் பயன்படுத்துங்கள். சொந்த எண் WhatsApp எல்லா திட்டத்திலும் உண்டு.' },
    ],
  },
  {
    slug: 'reminders',
    section: 'comms',
    title_en: 'Send reminders',
    title_ta: 'நினைவூட்டல்கள் அனுப்புங்கள்',
    summary_en: 'Automatic Tamil payment reminders by WhatsApp, and voice calls on Growth+.',
    summary_ta: 'WhatsApp மூலம் தானியங்கி தமிழ் நினைவூட்டல்; வளர்ச்சி+ இல் குரல் அழைப்பு.',
    body: [
      { kind: 'p', en: 'ChitDesk sends payment reminders in Tamil on schedule, signed with your business name, so you stop typing them by hand. Reminders go out over your connected WhatsApp number.', ta: 'ChitDesk திட்டப்படி தமிழில், உங்கள் வணிகப் பெயருடன் நினைவூட்டல்களை அனுப்பும் — கையால் தட்டச்சு செய்ய வேண்டாம். இணைத்த WhatsApp எண் வழியாகச் செல்லும்.' },
      { kind: 'tip', en: 'Voice reminder calls are available on the Growth plan and above.', ta: 'குரல் நினைவூட்டல் அழைப்புகள் வளர்ச்சி திட்டம் மற்றும் மேல்.' },
    ],
  },
  {
    slug: 'reports',
    section: 'reports',
    title_en: 'Reports',
    title_ta: 'அறிக்கைகள்',
    summary_en: 'Collection day, aging, day book, P&L, commission, forecasts and more.',
    summary_ta: 'வசூல் நாள், வயது, தினசரி கணக்கு, லாப/நஷ்டம், கமிஷன், கணிப்புகள்.',
    body: [
      { kind: 'p', en: 'The Reports hub has a card for each workflow: collection-day list, aging of dues, day book, foreman commission, member statement, chit P&L, payout forecast and comms analytics. Collection-day and member-statement are on every plan; the rest are on Growth and above.', ta: 'அறிக்கைகள் மையத்தில் ஒவ்வொரு பணிக்கும் ஒரு அட்டை: வசூல் நாள் பட்டியல், நிலுவை வயது, தினசரி கணக்கு, ஃபோர்மேன் கமிஷன், உறுப்பினர் அறிக்கை, சீட்டு லாப/நஷ்டம், பணம் வழங்கல் கணிப்பு, தொடர்பு பகுப்பாய்வு. வசூல் நாள் + உறுப்பினர் அறிக்கை எல்லா திட்டத்திலும்; மற்றவை வளர்ச்சி+.' },
    ],
  },
  {
    slug: 'billing-plans',
    section: 'account',
    title_en: 'Your plan, usage and upgrading',
    title_ta: 'உங்கள் திட்டம், பயன்பாடு & மேம்படுத்தல்',
    summary_en: 'See your plan, how close you are to its limits, and how to change it.',
    summary_ta: 'உங்கள் திட்டம், வரம்புக்கு எவ்வளவு அருகில், எப்படி மாற்றுவது.',
    body: [
      { kind: 'p', en: 'Settings → Billing shows your current plan, your usage against its limits (active chits, members, staff), and your monthly payment history. Switch between monthly and yearly billing — yearly gives you two months free.', ta: 'அமைப்புகள் → பில்லிங் உங்கள் நடப்புத் திட்டம், வரம்புகளுக்கு எதிரான பயன்பாடு (செயல் சீட்டுகள், உறுப்பினர்கள், பணியாளர்கள்), மாதாந்திர கட்டண வரலாறு காட்டும். மாதம்/ஆண்டு கட்டணம் மாற்றலாம் — ஆண்டுக்கட்டணத்தில் இரண்டு மாதம் இலவசம்.' },
      { kind: 'tip', en: 'If a feature is locked, you’ll see which plan unlocks it. Upgrades take effect immediately after payment.', ta: 'ஒரு அம்சம் பூட்டப்பட்டிருந்தால், எந்தத் திட்டம் திறக்கும் என்று காட்டப்படும். கட்டணத்திற்குப் பிறகு மேம்பாடு உடனே செயல்படும்.' },
    ],
  },
];
